/* Mapa de papéis do AVA -> perfis do painel de Inteligência Educacional.
   ÚNICO ponto de verdade. Ver decisão D3 do plano de integração.

   É uma ALLOWLIST: papel que não estiver aqui não tem acesso.

   Os nove papéis de access_roles, conferidos no banco em 04/08/2026 (pendência
   P1b, encerrada):

     1 Administrador Educandus  G    2 Administrador do Grupo     U
     3 Administrador de Regional R   4 Administrador Escolar      E
     5 Coordenador Pedagógico   E    6 Professor                  P
     7 Estudante                A    8 Responsável                S
     9 Visitante                V

   Quem manda no acesso é o backend, e ele decide pelo `type`, não pelo id:
   EscopoAnalyticsService::HIERARQUIA aceita G, U, R, E, P. Este mapa espelha
   isso — os papéis de fora (7, 8, 9) tomariam 403 em toda rota, então liberar
   aqui só produziria um painel vazio com cara de painel quebrado.

   Por que o 8 (Responsável) fica fora, mesmo sendo adulto: as rotas
   /aluno/:id/execucao, /aluno/:id/tempo-online e /escola/:id/inativos devolvem
   dado NOMINAL de aluno, e o recorte do mart é por turma/escola — não existe
   recorte "os meus filhos". Dar acesso ao responsável mostraria a turma
   inteira. Painel para família é outro produto, não uma linha nesta tabela.

   ---------------------------------------------------------------------------
   QUEM MANDA É O PAPEL ATIVO, E SÓ ELE (decisão do Kilson, 04/08/2026)
   ---------------------------------------------------------------------------
   Até aqui, um usuário que acumulasse papéis (há caso real de Gestor de rede +
   Administrador escolar) caía numa tela de seleção de visão, e o painel somava
   os perfis de TODOS os seus papéis. Não é o comportamento desejado: o usuário
   vê a visão do perfil em que está setado no AVA naquele momento, e nada além.

   A fonte é `general.active_roles.role_id` do Redux. Duas coisas medidas no
   código do AVA em 04/08/2026, que sustentam a decisão:

     - `active_roles` é sempre `user_roles[0]` (baseActions), e
       `User_role::roleUser()` ordena por `principal DESC` — ou seja, o papel
       ativo é o papel PRINCIPAL do usuário, não um sorteio;
     - o AVA não tem troca de papel: o HeaderMenu só EXIBE o `role_name` (item
       `disabled`), e o ModalGroupRegion troca grupo/região/escola preservando o
       `role_id`. Então o papel ativo é estável durante a sessão.

   Consequência de projeto: não existe mais "perfis disponíveis" no plural, nem
   escolha guardada em estado. Um papel, um perfil. Se o AVA algum dia ganhar
   troca de papel, o painel acompanha sozinho — ele já lê o papel ativo. */

export const PERFIL_POR_ROLE = {
  // Administrador Educandus: irrestrito no backend, então não havia um perfil
  // óbvio para ele — era o último papel que ainda justificaria uma tela de
  // escolha, e a decisão foi não ter nenhuma. Entra pelo painel de REDE, o mais
  // amplo dos três. O `escopo: "todos"` continua dizendo que ele não é
  // recortado por grupo/região/escola no recorte de interface.
  1: { perfil: "gestor", escopo: "todos" },
  2: { perfil: "gestor", escopo: "rede" }, // administrador de grupo
  3: { perfil: "gestor", escopo: "regiao" }, // administrador de regional (D3.1)
  4: { perfil: "admin", escopo: "escola" }, // administrador escolar
  // Coordenador Pedagógico: mesmo `type` E do papel 4, logo o backend já lhe
  // dava o mesmo recorte de escola — o front era o único lado que negava.
  5: { perfil: "admin", escopo: "escola" },
  6: { perfil: "professor", escopo: "turma" } // professor
};

/** Perfil do papel ATIVO — o que o usuário vê, e a única resposta possível.
    null quando o papel ativo não dá acesso ao painel. */
export const perfilDoRole = activeRoleId =>
  PERFIL_POR_ROLE[activeRoleId] ? PERFIL_POR_ROLE[activeRoleId].perfil : null;

/** true se o papel ATIVO dá acesso ao painel.

    Deliberadamente NÃO olha os outros papéis do usuário: quem está no AVA como
    Estudante não vê o menu da Inteligência Educacional, mesmo tendo um papel de
    professor guardado. Antes olhava — e o item de menu aparecia para quem não
    estava naquele papel, levando a um painel que não era o dele. */
export const temAcesso = activeRoleId =>
  PERFIL_POR_ROLE[activeRoleId] !== undefined;

/** Escopo de dados do papel ativo — "todos" | "rede" | "regiao" | "escola" |
    "turma". Usado para recortar as chamadas de API (e, no papel 3, fixar a
    região). */
export const escopoDoRole = activeRoleId =>
  PERFIL_POR_ROLE[activeRoleId] ? PERFIL_POR_ROLE[activeRoleId].escopo : null;
